import React, { useState, useEffect, FC } from 'react'
import {
  useDataProvider,
  useNotify,
  Button,
  useRefresh,
  useRecordContext,
} from 'react-admin'
import {
  Box,
  Typography,
  Paper,
  TextField,
  IconButton,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
  Checkbox,
  Divider,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormHelperText,
  FormControl,
} from '@mui/material'
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  Save as SaveIcon,
  Visibility as VisibilityIcon,
  Upload as UploadIcon,
} from '@mui/icons-material'
import { QuestionnaireType } from './OrganisationRiskQuestionnaireDirectory'
import QuestionnaireFormLayout from '../components/QuestionnaireFormLayout'

// Define the field and section types
interface QuestionnaireField {
  id?: string
  label: string
  source: string
  required?: boolean
  value?: string
  fieldType?: 'yesno' | 'dropdown' | 'attachment' | 'text'
  options?: Array<{
    text: string
    score?: number // Score from 1-20 for Risk Assessment options
  }> // For dropdown fields
  allow_vendor_comment?: boolean // Whether to allow vendor comments for this field
}

interface QuestionnaireSection {
  id?: string
  title: string
  fields: QuestionnaireField[]
}

// Custom renderer that shows the field type (yes/no or dropdown) but disables interaction
const previewFieldRenderer = (field: QuestionnaireField) => {
  // For yes/no fields (used in prequalification questionnaires)
  if (field.fieldType === 'yesno') {
    return (
      <FormControl fullWidth>
        <TextField
          select
          name={field.source}
          fullWidth
          variant="outlined"
          required={field.required}
          disabled={false}
          InputProps={{
            readOnly: false,
          }}
          SelectProps={{
            native: true,
          }}
        >
          <option value="">Select an option</option>
          <option value="yes">Yes</option>
          <option value="no">No</option>
        </TextField>
        <FormHelperText>
          {field.allow_vendor_comment
            ? 'Vendor Comment Allowed: Yes'
            : 'Vendor Comment Allowed: No'}
        </FormHelperText>
        <FormHelperText>
          {field.required ? 'Required' : 'Optional'}
        </FormHelperText>
      </FormControl>
    )
  }

  // For dropdown fields (used in risk assessments)
  if (field.fieldType === 'dropdown') {
    return (
      <FormControl fullWidth>
        <TextField
          select
          name={field.source}
          fullWidth
          variant="outlined"
          required={field.required}
          disabled={false}
          InputProps={{
            readOnly: false,
          }}
          SelectProps={{
            native: true,
          }}
        >
          <option value="">Select an option</option>
          {field.options && field.options.length > 0 ? (
            field.options.map((option, index) => (
              <option key={index} value={option.text}>
                {option.text}
                {option.score !== undefined
                  ? ` (Score: ${
                      typeof option.score === 'number'
                        ? option.score.toFixed(1)
                        : option.score
                    })`
                  : ''}
              </option>
            ))
          ) : (
            <option value="" disabled>
              No options available
            </option>
          )}
        </TextField>
        <FormHelperText>
          {field.allow_vendor_comment
            ? 'Vendor Comment Allowed: Yes'
            : 'Vendor Comment Allowed: No'}
        </FormHelperText>
        <FormHelperText>
          {field.required ? 'Required' : 'Optional'}
        </FormHelperText>
      </FormControl>
    )
  }

  // For attachment fields
  if (field.fieldType === 'attachment') {
    return (
      <Box>
        <Button
          variant="outlined"
          component="label"
          disabled
          startIcon={<UploadIcon />}
        >
          <Typography>Add Attachment</Typography>
        </Button>
        <FormHelperText>
          {field.allow_vendor_comment
            ? 'Vendor Comment Allowed: Yes'
            : 'Vendor Comment Allowed: No'}
        </FormHelperText>
        <FormHelperText>
          {field.required ? 'Required' : 'Optional'} - Allows vendors to upload
          PDF or Word documents
        </FormHelperText>
      </Box>
    )
  }

  // For text fields
  if (field.fieldType === 'text') {
    return (
      <FormControl fullWidth>
        <TextField
          name={field.source}
          fullWidth
          variant="outlined"
          required={field.required}
          disabled={false}
          multiline
          rows={3}
          InputProps={{
            readOnly: false,
          }}
        />
        <FormHelperText>
          {field.allow_vendor_comment
            ? 'Vendor Comment Allowed: Yes'
            : 'Vendor Comment Allowed: No'}
        </FormHelperText>
        <FormHelperText>
          {field.required ? 'Required' : 'Optional'}
        </FormHelperText>
      </FormControl>
    )
  }

  // Default text field for other fields
  return (
    <FormControl fullWidth>
      <TextField
        name={field.source}
        fullWidth
        variant="outlined"
        required={field.required}
        disabled
      />
      <FormHelperText>
        {field.allow_vendor_comment
          ? 'Vendor Comment Allowed: Yes'
          : 'Vendor Comment Allowed: No'}
      </FormHelperText>
      <FormHelperText>
        {field.required ? 'Required' : 'Optional'}
      </FormHelperText>
    </FormControl>
  )
}

// Preview questionnaire component
interface QuestionnairePreviewProps {
  questionnaireSections: QuestionnaireSection[]
  questionnaireName: string
  questionnaireDescription: string
  allowAdditionalDocumentUploads: boolean
}

const QuestionnairePreview: FC<QuestionnairePreviewProps> = ({
  questionnaireSections,
  questionnaireName,
  questionnaireDescription,
  allowAdditionalDocumentUploads,
}) => {
  return (
    <QuestionnaireFormLayout
      questionnaireSections={questionnaireSections}
      questionnaireName={questionnaireName}
      questionnaireDescription={questionnaireDescription}
      readOnly
      renderField={previewFieldRenderer}
      allowAdditionalDocumentUploads={allowAdditionalDocumentUploads}
    />
  )
}

// Field validation state
interface FieldValidation {
  error: boolean
  helperText: string
  showHelper: boolean
}

// Generate a unique ID
const generateUniqueId = (): string => {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
}

interface QuestionnaireFormProps {
  questionnaireId?: number
  isModal?: boolean
  onClose?: (updatedQuestionnaire?: any) => void
  customEndpoint?: (
    organisationId: number | undefined,
    questionnaireId: number | undefined
  ) => string
  formType?: string
  questionnaireTypeLabel?: string
  questionnaireType?: QuestionnaireType
}

const QuestionnaireForm = ({
  questionnaireId,
  isModal = false,
  onClose,
  customEndpoint,
  formType = 'Questionnaire',
  questionnaireTypeLabel,
  questionnaireType = QuestionnaireType.RISK,
}: QuestionnaireFormProps) => {
  const dataProvider = useDataProvider()
  const notify = useNotify()
  const refresh = useRefresh()
  const record = useRecordContext()

  // State to store the questionnaire sections
  const [questionnaireSections, setQuestionnaireSections] = useState<
    QuestionnaireSection[]
  >([])
  const [originalData, setOriginalData] = useState<any>(null)
  const [questionnaireName, setQuestionnaireName] = useState('')
  const [questionnaireDescription, setQuestionnaireDescription] = useState('')
  const [questionnaireExpiryTime, setQuestionnaireExpiryTime] =
    useState('01:00')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [isPreviewOpen, setIsPreviewOpen] = useState(false)
  const [localQuestionnaireType, setLocalQuestionnaireType] = useState<
    QuestionnaireType | undefined
  >(undefined)

  // State for field validation
  const [fieldValidations, setFieldValidations] = useState<{
    [key: string]: FieldValidation
  }>({})

  // State for field type selection dialog
  const [isFieldTypeDialogOpen, setIsFieldTypeDialogOpen] = useState(false)
  const [currentSectionIndex, setCurrentSectionIndex] = useState<number | null>(
    null
  )

  // State for allowing additional document uploads at the end of questionnaire
  const [allowAdditionalDocumentUploads, setAllowAdditionalDocumentUploads] =
    useState(false)

  // Update field validation state
  const updateFieldValidation = (key: string, validation: FieldValidation) => {
    setFieldValidations((prev) => ({
      ...prev,
      [key]: validation,
    }))
  }

  // Helper function to get the effective questionnaire type
  const getEffectiveQuestionnaireType = (): QuestionnaireType => {
    return localQuestionnaireType !== undefined
      ? localQuestionnaireType
      : questionnaireType
  }

  // Generate a unique field source based on section and field index
  const generateFieldSource = (
    sectionIndex: number,
    fieldIndex: number
  ): string => {
    return `questionnaire_section_${sectionIndex + 1}_field_${fieldIndex + 1}`
  }

  // Check if a field label is unique within its section
  const isFieldLabelUnique = (
    sectionIndex: number,
    fieldIndex: number,
    label: string
  ): boolean => {
    if (!label.trim()) return true // Empty labels are handled by a different validation

    const section = questionnaireSections[sectionIndex]
    return !section.fields.some(
      (field, idx) => idx !== fieldIndex && field.label.trim() === label.trim()
    )
  }

  // Validate a field label
  const validateFieldLabel = (
    sectionIndex: number,
    fieldIndex: number,
    label: string
  ): FieldValidation => {
    if (!label.trim()) {
      return {
        error: true,
        helperText: 'Field label is required',
        showHelper: true,
      }
    }

    if (!isFieldLabelUnique(sectionIndex, fieldIndex, label)) {
      return {
        error: true,
        helperText: 'Field label must be unique within this section',
        showHelper: true,
      }
    }

    return {
      error: false,
      helperText: '',
      showHelper: false,
    }
  }

  // Check if all fields in a section have valid labels
  const areSectionFieldsValid = (sectionIndex: number): boolean => {
    const section = questionnaireSections[sectionIndex]
    return section.fields.every((field, fieldIndex) => {
      const validation = validateFieldLabel(
        sectionIndex,
        fieldIndex,
        field.label
      )
      return !validation.error
    })
  }

  // Validate a section title
  const validateSectionTitle = (
    sectionIndex: number,
    title: string
  ): FieldValidation => {
    if (!title.trim()) {
      return {
        error: true,
        helperText: 'Section title is required',
        showHelper: true,
      }
    }

    return {
      error: false,
      helperText: '',
      showHelper: false,
    }
  }

  // Check if all sections have valid titles
  const areSectionTitlesValid = (): boolean => {
    return questionnaireSections.every((section, sectionIndex) => {
      const validation = validateSectionTitle(sectionIndex, section.title)
      return !validation.error
    })
  }

  // Check if all options in Risk Assessment questionnaires have scores
  const areAllOptionsValid = (): boolean => {
    const effectiveType = getEffectiveQuestionnaireType()

    // Only validate scores for Risk Assessment questionnaires
    if (effectiveType !== QuestionnaireType.RISK) {
      return true
    }

    return questionnaireSections.every((section) => {
      return section.fields.every((field) => {
        // Skip fields that aren't dropdowns or don't have options
        if (
          field.fieldType !== 'dropdown' ||
          !field.options ||
          field.options.length === 0
        ) {
          return true
        }

        // Check that all options have scores
        return field.options.every((option) => option.score !== undefined)
      })
    })
  }

  // Calculate min and max risk scores for Risk Assessment questionnaires
  const calculateRiskScores = (): {
    minRiskScore: number | null
    maxRiskScore: number | null
  } => {
    const effectiveType = getEffectiveQuestionnaireType()

    // Only calculate scores for Risk Assessment questionnaires
    if (effectiveType !== QuestionnaireType.RISK) {
      return { minRiskScore: null, maxRiskScore: null }
    }

    // Initialize arrays to store all min and max scores
    const minScores: number[] = []
    const maxScores: number[] = []

    // Iterate through all sections and fields
    questionnaireSections.forEach((section) => {
      section.fields.forEach((field) => {
        // Skip fields that aren't dropdowns or don't have options
        if (
          field.fieldType !== 'dropdown' ||
          !field.options ||
          field.options.length === 0
        ) {
          return
        }

        // Get all scores for this field
        const scores = field.options
          .map((option) => option.score)
          .filter((score): score is number => score !== undefined)

        // If there are scores, add the min and max to our arrays
        if (scores.length > 0) {
          minScores.push(Math.min(...scores))
          maxScores.push(Math.max(...scores))
        }
      })
    })

    // Calculate the sum of min and max scores
    // Always use 0 as the minimum risk score
    const minRiskScore = 0
    const maxRiskScore =
      maxScores.length > 0
        ? maxScores.reduce((sum, score) => sum + score, 0)
        : null

    return { minRiskScore, maxRiskScore }
  }

  // Convert time in hh:mm format to minutes
  const convertTimeToMinutes = (time: string): number => {
    if (!time) return 60 // Default to 60 minutes (1 hour)

    const [hours, minutes] = time.split(':').map(Number)
    return hours * 60 + minutes
  }

  // Check if all sections have valid titles and fields
  const isQuestionnaireValid = (): boolean => {
    // Check if expiry time is valid
    const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/
    const isExpiryTimeValid = timeRegex.test(questionnaireExpiryTime)

    return (
      questionnaireName.trim() !== '' &&
      areSectionTitlesValid() &&
      questionnaireSections.every((_, sectionIndex) => {
        return areSectionFieldsValid(sectionIndex)
      }) &&
      areAllOptionsValid() &&
      isExpiryTimeValid
    )
  }

  // Load the organisation's questionnaire configuration
  useEffect(() => {
    const loadQuestionnaireForm = async () => {
      try {
        setLoading(true)
        const organisationId = record?.id

        // If questionnaireId is provided, load that specific questionnaire
        let endpoint
        if (customEndpoint) {
          endpoint = customEndpoint(
            organisationId ? Number(organisationId) : undefined,
            questionnaireId
          )
        } else if (questionnaireId) {
          endpoint = organisationId
            ? `questionnaires/${questionnaireId}/organisations/${organisationId}`
            : `organisations/current/questionnaires/${questionnaireId}`
        } else {
          endpoint = organisationId
            ? `questionnaires/organisations/${organisationId}`
            : 'organisations/current/questionnaires'
        }

        const response = await dataProvider.custom(endpoint, {
          method: 'GET',
        })

        // Store the original data for use in the update call
        setOriginalData(response.data)

        // Set questionnaire name, description, and expiry hours
        if (response.data?.name) {
          setQuestionnaireName(response.data.name)
        }
        if (response.data?.description) {
          setQuestionnaireDescription(response.data.description)
        }
        if (response.data?.expiry_minutes !== undefined) {
          // Convert minutes to hh:mm format
          const hours = Math.floor(response.data.expiry_minutes / 60)
          const minutes = response.data.expiry_minutes % 60
          const formattedHours = hours.toString().padStart(2, '0')
          const formattedMinutes = minutes.toString().padStart(2, '0')
          setQuestionnaireExpiryTime(`${formattedHours}:${formattedMinutes}`)
        }

        // Set allow additional document uploads option if it's provided in the response
        if (response.data?.allow_additional_document_uploads !== undefined) {
          setAllowAdditionalDocumentUploads(
            response.data.allow_additional_document_uploads
          )
        }

        // Set questionnaire type if it's provided in the response
        console.log('Questionnaire type from API:', response.data?.type)
        if (response.data?.type) {
          // Convert string type to enum value
          const typeValue =
            response.data.type === 'preliminary'
              ? QuestionnaireType.PRELIMINARY
              : QuestionnaireType.RISK
          setLocalQuestionnaireType(typeValue)
        }

        if (response.data?.questionnaire) {
          // Ensure all sections and fields have unique IDs
          const fieldsWithIds = (section) =>
            section.fields.map((field) => {
              // Convert old format options (strings) to new format (objects with text and score)
              const updatedField = {
                ...field,
                id: field.id ?? generateUniqueId(),
              }

              // If this is a dropdown field with options
              if (field.fieldType === 'dropdown' && field.options) {
                // Check if options are in old format (strings)
                if (
                  field.options.length > 0 &&
                  typeof field.options[0] === 'string'
                ) {
                  updatedField.options = field.options.map((option) => ({
                    text: option,
                    score: undefined,
                  }))
                }
              }

              return updatedField
            })
          const questionnaireWithIds = response.data.questionnaire.map(
            (section, sectionIndex) => {
              return {
                ...section,
                id: section.id ?? generateUniqueId(),
                fields: fieldsWithIds(section),
              }
            }
          )
          setQuestionnaireSections(questionnaireWithIds)
        } else {
          // Initialise with an empty array if no configuration is found
          setQuestionnaireSections([])

          // If originalData exists but doesn't have questionnaire, add it as empty array
          if (response.data) {
            setOriginalData({
              ...response.data,
              questionnaire: [],
            })
          }
        }
      } catch (error) {
        console.error(`Error loading ${formType.toLowerCase()}:`, error)
        notify(`Error loading ${formType.toLowerCase()} configuration`, {
          type: 'error',
        })
        // Initialize with an empty array on error
        setQuestionnaireSections([])
        // Initialize originalData with an empty object
        setOriginalData({ questionnaire: [] })
      } finally {
        setLoading(false)
      }
    }

    loadQuestionnaireForm()
  }, [
    dataProvider,
    notify,
    setOriginalData,
    record,
    questionnaireId,
    customEndpoint,
    formType,
  ])

  // Save the questionnaire configuration
  const saveQuestionnaireForm = async () => {
    // Check if questionnaire name is provided
    if (!questionnaireName.trim()) {
      notify('Questionnaire name is required', { type: 'warning' })
      return
    }

    // Validate the questionnaire before saving
    if (!isQuestionnaireValid()) {
      // Check specifically if the issue is with missing scores
      if (
        getEffectiveQuestionnaireType() === QuestionnaireType.RISK &&
        !areAllOptionsValid()
      ) {
        notify(
          'All options in Risk Assessment questionnaires must have scores (1-20)',
          { type: 'warning' }
        )
      } else {
        notify('Please fix validation errors before saving', {
          type: 'warning',
        })
      }

      // Show all validation errors
      const newValidations = { ...fieldValidations }

      // Validate section titles
      questionnaireSections.forEach((section, sectionIndex) => {
        const titleKey = `section-${sectionIndex}-title`
        const titleValidation = validateSectionTitle(
          sectionIndex,
          section.title
        )
        newValidations[titleKey] = {
          ...titleValidation,
          showHelper: true,
        }

        // Validate field labels
        section.fields.forEach((field, fieldIndex) => {
          const key = `${sectionIndex}-${fieldIndex}-label`
          const validation = validateFieldLabel(
            sectionIndex,
            fieldIndex,
            field.label
          )
          newValidations[key] = {
            ...validation,
            showHelper: true,
          }
        })
      })
      setFieldValidations(newValidations)

      return
    }

    try {
      setSaving(true)
      const organisationId = record?.id

      // Determine the endpoint based on whether we're editing a specific questionnaire
      let endpoint
      if (customEndpoint) {
        endpoint = customEndpoint(
          organisationId ? Number(organisationId) : undefined,
          questionnaireId
        )
      } else if (questionnaireId) {
        endpoint = organisationId
          ? `questionnaires/${questionnaireId}/organisations/${organisationId}`
          : `organisations/current/questionnaires/${questionnaireId}`
      } else {
        endpoint = organisationId
          ? `questionnaires/organisations/${organisationId}`
          : 'organisations/current/questionnaires'
      }

      // Calculate risk scores for Risk Assessment questionnaires
      const { minRiskScore, maxRiskScore } = calculateRiskScores()

      const response = await dataProvider.custom(endpoint, {
        method: 'PUT',
        body: JSON.stringify({
          questionnaire: questionnaireSections,
          name: questionnaireName || formType,
          description: questionnaireDescription || '',
          type: localQuestionnaireType || questionnaireType,
          min_risk_score: minRiskScore,
          max_risk_score: maxRiskScore,
          expiry_minutes: convertTimeToMinutes(questionnaireExpiryTime),
          allow_additional_document_uploads: allowAdditionalDocumentUploads,
        }),
      })

      notify(`${formType} configuration saved`, { type: 'success' })

      // Create an updated questionnaire object with the new values
      const updatedQuestionnaire = {
        id: questionnaireId,
        name: questionnaireName || formType,
        description: questionnaireDescription || '',
        type: localQuestionnaireType || questionnaireType,
        questionnaire: questionnaireSections,
        min_risk_score: minRiskScore,
        max_risk_score: maxRiskScore,
        expiry_minutes: convertTimeToMinutes(questionnaireExpiryTime),
        allow_additional_document_uploads: allowAdditionalDocumentUploads,
        organisation_id: record?.id || response.data?.organisation_id,
        created_at: originalData?.created_at || response.data?.created_at,
        updated_at: response.data?.updated_at || new Date().toISOString(),
      }

      // If this is a modal, call the onClose callback with the updated questionnaire
      if (isModal && onClose) {
        onClose(updatedQuestionnaire)
      } else {
        refresh()
      }
    } catch (error) {
      console.error(`Error saving ${formType.toLowerCase()}:`, error)
      // Try to extract the specific error message from the backend response
      let errorMessage = `Error saving ${formType.toLowerCase()} configuration`
      if (error && typeof error === 'object') {
        if (
          'body' in error &&
          error.body &&
          typeof error.body === 'object' &&
          'detail' in error.body
        ) {
          errorMessage = error.body.detail as string
        } else if ('message' in error && typeof error.message === 'string') {
          errorMessage = error.message
        }
      }
      notify(errorMessage, { type: 'error' })
    } finally {
      setSaving(false)
    }
  }

  // Check if the maximum number of questions has been reached
  const hasReachedMaxQuestions = () => {
    // Count total fields across all sections
    const totalFields = questionnaireSections.reduce(
      (total, section) => total + section.fields.length,
      0
    )

    const effectiveType = getEffectiveQuestionnaireType()

    // Prequalification Questionnaires have a maximum of 10 questions
    if (effectiveType === QuestionnaireType.PRELIMINARY && totalFields >= 10) {
      return true
    }

    // Risk Assessments have a maximum of 100 questions
    if (effectiveType === QuestionnaireType.RISK && totalFields >= 100) {
      return true
    }

    return false
  }

  // Add a new section
  const addSection = () => {
    // Check if existing sections are valid before adding a new one
    if (questionnaireSections.length > 0 && !isQuestionnaireValid()) {
      notify('Please fix validation errors before adding a new section', {
        type: 'warning',
      })
      return
    }

    // Check if maximum questions limit has been reached
    if (hasReachedMaxQuestions()) {
      const effectiveType = getEffectiveQuestionnaireType()
      notify(
        `Maximum number of questions reached for ${
          effectiveType === QuestionnaireType.PRELIMINARY
            ? 'Prequalification Questionnaire'
            : 'Risk Assessment'
        }`,
        {
          type: 'warning',
        }
      )
      return
    }

    const newSectionIndex = questionnaireSections.length
    const newSection = {
      id: generateUniqueId(),
      title: 'New Section',
      fields: [], // Create an empty section without automatically adding a question
    }

    setQuestionnaireSections([...questionnaireSections, newSection])

    // Initialise validation for the new section title
    updateFieldValidation(`section-${newSectionIndex}-title`, {
      error: false,
      helperText: '',
      showHelper: false,
    })
  }

  // Delete a section
  const deleteSection = (sectionIndex: number) => {
    // Remove validations for this section
    const newValidations = { ...fieldValidations }

    // Remove section title validation
    delete newValidations[`section-${sectionIndex}-title`]

    // Remove field validations
    Object.keys(newValidations).forEach((key) => {
      if (key.startsWith(`${sectionIndex}-`)) {
        delete newValidations[key]
      }
    })

    // Update section indices for validations after the deleted section
    Object.keys(newValidations).forEach((key) => {
      if (key.startsWith('section-')) {
        const parts = key.split('-')
        const currentIndex = parseInt(parts[1], 10)
        if (currentIndex > sectionIndex) {
          const newKey = `section-${currentIndex - 1}-${parts[2]}`
          newValidations[newKey] = newValidations[key]
          delete newValidations[key]
        }
      } else {
        const parts = key.split('-')
        const currentIndex = parseInt(parts[0], 10)
        if (currentIndex > sectionIndex) {
          const newKey = `${currentIndex - 1}-${parts[1]}-${parts[2]}`
          newValidations[newKey] = newValidations[key]
          delete newValidations[key]
        }
      }
    })

    setFieldValidations(newValidations)

    // Update currentSectionIndex if it's affected by the deletion
    if (currentSectionIndex !== null) {
      if (currentSectionIndex === sectionIndex) {
        // If the deleted section is the current section, close the field type dialog
        setCurrentSectionIndex(null)
        setIsFieldTypeDialogOpen(false)
      } else if (currentSectionIndex > sectionIndex) {
        // If the deleted section is before the current section, decrement currentSectionIndex
        setCurrentSectionIndex(currentSectionIndex - 1)
      }
    }

    setQuestionnaireSections(
      questionnaireSections.filter((_, index) => index !== sectionIndex)
    )
  }

  // Update a section title
  const updateSectionTitle = (sectionIndex: number, title: string) => {
    const updatedSections = [...questionnaireSections]
    updatedSections[sectionIndex].title = title
    setQuestionnaireSections(updatedSections)

    // Validate the section title
    const validation = validateSectionTitle(sectionIndex, title)
    updateFieldValidation(`section-${sectionIndex}-title`, validation)
  }

  // Add a new field to a section
  const addField = (sectionIndex: number) => {
    // Check if existing fields in this section are valid
    if (!areSectionFieldsValid(sectionIndex)) {
      notify('Please fix validation errors before adding a new field', {
        type: 'warning',
      })
      return
    }

    // Check if maximum questions limit has been reached
    if (hasReachedMaxQuestions()) {
      const effectiveType = getEffectiveQuestionnaireType()
      notify(
        `Maximum number of questions reached for ${
          effectiveType === QuestionnaireType.PRELIMINARY
            ? 'Prequalification Questionnaire'
            : 'Risk Assessment'
        }`,
        {
          type: 'warning',
        }
      )
      return
    }

    // Open the field type selection dialog
    setCurrentSectionIndex(sectionIndex)
    setIsFieldTypeDialogOpen(true)
  }

  // Add a field of the selected type
  const addFieldOfType = (
    sectionIndex: number,
    fieldType: 'yesno' | 'dropdown' | 'attachment' | 'text'
  ) => {
    const updatedSections = [...questionnaireSections]

    // Check if the section exists before accessing its fields
    if (!updatedSections[sectionIndex]) {
      notify('Section not found. It may have been deleted.', { type: 'error' })
      return
    }

    const newFieldIndex = updatedSections[sectionIndex].fields.length

    // Generate a unique source for the new field
    const newFieldSource = generateFieldSource(sectionIndex, newFieldIndex)

    const effectiveType = getEffectiveQuestionnaireType()

    // Create the field based on the selected type
    updatedSections[sectionIndex].fields.push({
      id: generateUniqueId(),
      label: 'Question Label',
      source: newFieldSource,
      required: effectiveType === QuestionnaireType.PRELIMINARY,
      fieldType,
      options:
        fieldType === 'dropdown' && effectiveType === QuestionnaireType.RISK
          ? []
          : undefined,
    })

    setQuestionnaireSections(updatedSections)

    // Initialize validation for the new field
    updateFieldValidation(`${sectionIndex}-${newFieldIndex}-label`, {
      error: false,
      helperText: '',
      showHelper: false,
    })
  }

  // Delete a field from a section
  const deleteField = (sectionIndex: number, fieldIndex: number) => {
    // Remove validation for this field
    const newValidations = { ...fieldValidations }
    delete newValidations[`${sectionIndex}-${fieldIndex}-label`]

    // Update validations for fields that come after this one
    Object.keys(newValidations).forEach((key) => {
      const parts = key.split('-')
      if (
        parts[0] === sectionIndex.toString() &&
        parseInt(parts[1], 10) > fieldIndex
      ) {
        const newKey = `${parts[0]}-${parseInt(parts[1], 10) - 1}-${parts[2]}`
        newValidations[newKey] = newValidations[key]
        delete newValidations[key]
      }
    })

    setFieldValidations(newValidations)

    const updatedSections = [...questionnaireSections]
    updatedSections[sectionIndex].fields = updatedSections[
      sectionIndex
    ].fields.filter((_, index) => index !== fieldIndex)

    // Regenerate sources for all fields in this section to maintain consistency
    updatedSections[sectionIndex].fields.forEach((fieldItem, idx) => {
      fieldItem.source = generateFieldSource(sectionIndex, idx)
    })

    setQuestionnaireSections(updatedSections)
  }

  // Update a field
  const updateField = (
    sectionIndex: number,
    fieldIndex: number,
    field: Partial<QuestionnaireField>
  ) => {
    const updatedSections = [...questionnaireSections]
    const updatedField = {
      ...updatedSections[sectionIndex].fields[fieldIndex],
      ...field,
    }

    // If label is being updated, validate it
    if (field.label !== undefined) {
      const validation = validateFieldLabel(
        sectionIndex,
        fieldIndex,
        field.label
      )
      updateFieldValidation(`${sectionIndex}-${fieldIndex}-label`, validation)
    }

    updatedField.source = generateFieldSource(sectionIndex, fieldIndex)

    updatedSections[sectionIndex].fields[fieldIndex] = updatedField
    setQuestionnaireSections(updatedSections)
  }

  if (loading) {
    return <Typography>Loading...</Typography>
  }

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', overflowX: 'visible' }}>
      {/* Questionnaire Name, Type and Description */}
      <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Questionnaire Details
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
              Type:{' '}
              {questionnaireTypeLabel ||
                (getEffectiveQuestionnaireType() ===
                QuestionnaireType.PRELIMINARY
                  ? 'Prequalification Questionnaire'
                  : 'Risk Assessment')}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Questionnaire Name"
              value={questionnaireName}
              onChange={(e) => setQuestionnaireName(e.target.value)}
              fullWidth
              variant="outlined"
              margin="normal"
              required
              error={!questionnaireName.trim()}
              helperText={
                !questionnaireName.trim()
                  ? 'Questionnaire name is required'
                  : ''
              }
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Questionnaire Description"
              value={questionnaireDescription}
              onChange={(e) => setQuestionnaireDescription(e.target.value)}
              fullWidth
              variant="outlined"
              margin="normal"
              multiline
              rows={3}
              placeholder="Enter a description for this questionnaire"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Expiry Time (hh:mm)"
              value={questionnaireExpiryTime}
              onChange={(e) => {
                // Allow only valid time format (hh:mm)
                const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/
                if (
                  timeRegex.test(e.target.value) ||
                  e.target.value === '' ||
                  e.target.value.length <= 5
                ) {
                  setQuestionnaireExpiryTime(e.target.value)
                }
              }}
              type="time"
              fullWidth
              variant="outlined"
              margin="normal"
              helperText="Time (hours:minutes) until the questionnaire link expires (default: 01:00)"
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Preview Dialog */}
      <Dialog
        open={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        fullWidth
        maxWidth="md"
        aria-labelledby="questionnaire-preview-dialog-title"
      >
        <DialogTitle id="questionnaire-preview-dialog-title">
          Questionnaire Preview
        </DialogTitle>
        <DialogContent>
          {questionnaireSections.length > 0 ? (
            <QuestionnairePreview
              questionnaireSections={questionnaireSections}
              questionnaireName={questionnaireName}
              questionnaireDescription={questionnaireDescription}
              allowAdditionalDocumentUploads={allowAdditionalDocumentUploads}
            />
          ) : (
            <Typography>No questionnaire sections to preview.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsPreviewOpen(false)} variant="outlined">
            <Typography>Close</Typography>
          </Button>
        </DialogActions>
      </Dialog>

      {/* Field Type Selection Dialog */}
      <Dialog
        open={isFieldTypeDialogOpen}
        onClose={() => setIsFieldTypeDialogOpen(false)}
        aria-labelledby="field-type-dialog-title"
      >
        <DialogTitle id="field-type-dialog-title">
          Select Field Type
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => {
                  if (currentSectionIndex !== null) {
                    addFieldOfType(currentSectionIndex, 'yesno')
                    setIsFieldTypeDialogOpen(false)
                  }
                }}
                sx={{ justifyContent: 'flex-start', textAlign: 'left', py: 2 }}
              >
                <Box>
                  <Typography variant="subtitle1">Yes/No Question</Typography>
                  <Typography variant="body2" color="textSecondary">
                    A question with Yes or No answer options
                  </Typography>
                </Box>
              </Button>
            </Grid>
            {getEffectiveQuestionnaireType() === QuestionnaireType.RISK && (
              <>
                <Grid item xs={12}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => {
                      if (currentSectionIndex !== null) {
                        addFieldOfType(currentSectionIndex, 'dropdown')
                        setIsFieldTypeDialogOpen(false)
                      }
                    }}
                    sx={{
                      justifyContent: 'flex-start',
                      textAlign: 'left',
                      py: 2,
                    }}
                  >
                    <Box>
                      <Typography variant="subtitle1">
                        Multiple Choice
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        A question with multiple answer options
                      </Typography>
                    </Box>
                  </Button>
                </Grid>
                <Grid item xs={12}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => {
                      if (currentSectionIndex !== null) {
                        addFieldOfType(currentSectionIndex, 'text')
                        setIsFieldTypeDialogOpen(false)
                      }
                    }}
                    sx={{
                      justifyContent: 'flex-start',
                      textAlign: 'left',
                      py: 2,
                    }}
                  >
                    <Box>
                      <Typography variant="subtitle1">Text Box</Typography>
                      <Typography variant="body2" color="textSecondary">
                        Allow vendors to enter free-form text responses
                      </Typography>
                    </Box>
                  </Button>
                </Grid>
              </>
            )}
            <Grid item xs={12}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => {
                  if (currentSectionIndex !== null) {
                    addFieldOfType(currentSectionIndex, 'attachment')
                    setIsFieldTypeDialogOpen(false)
                  }
                }}
                sx={{ justifyContent: 'flex-start', textAlign: 'left', py: 2 }}
              >
                <Box>
                  <Typography variant="subtitle1">Add Attachment</Typography>
                  <Typography variant="body2" color="textSecondary">
                    Allow vendors to upload PDF, DOCX, XLSX, PNG, JPG files
                  </Typography>
                </Box>
              </Button>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setIsFieldTypeDialogOpen(false)}
            variant="outlined"
          >
            <Typography>Cancel</Typography>
          </Button>
        </DialogActions>
      </Dialog>

      {questionnaireSections.length > 0 && (
        <>
          {questionnaireSections.map((section, sectionIndex) => (
            <Accordion key={section.id} defaultExpanded sx={{ mb: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  width="100%"
                >
                  <Typography variant="subtitle1">{section.title}</Typography>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation()
                      deleteSection(sectionIndex)
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Box mb={2}>
                  {(() => {
                    const validationKey = `section-${sectionIndex}-title`
                    const validation = fieldValidations[validationKey] || {
                      error: false,
                      helperText: '',
                      showHelper: false,
                    }

                    return (
                      <TextField
                        label="Section Title"
                        value={section.title}
                        onChange={(e) =>
                          updateSectionTitle(sectionIndex, e.target.value)
                        }
                        onFocus={() => {
                          if (validation.showHelper) {
                            updateFieldValidation(validationKey, {
                              ...validation,
                              showHelper: false,
                            })
                          }

                          // Clear default section title when user focuses on it
                          if (section.title === 'New Section') {
                            updateSectionTitle(sectionIndex, '')
                          }
                        }}
                        onBlur={() => {
                          const newValidation = validateSectionTitle(
                            sectionIndex,
                            section.title
                          )
                          updateFieldValidation(validationKey, {
                            ...newValidation,
                            showHelper: true,
                          })
                        }}
                        fullWidth
                        variant="outlined"
                        size="small"
                        error={validation.error && validation.showHelper}
                        helperText={
                          validation.showHelper ? validation.helperText : ''
                        }
                      />
                    )
                  })()}
                </Box>

                <Divider sx={{ mb: 2 }} />

                <Typography variant="subtitle2" gutterBottom>
                  Question
                </Typography>

                {section.fields.map((field, fieldIndex) => {
                  const validationKey = `${sectionIndex}-${fieldIndex}-label`
                  const validation = fieldValidations[validationKey] || {
                    error: false,
                    helperText: '',
                    showHelper: false,
                  }

                  return (
                    <Paper key={field.id} elevation={1} sx={{ p: 2, mb: 2 }}>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <TextField
                            label="Question Label"
                            value={field.label}
                            onChange={(e) =>
                              updateField(sectionIndex, fieldIndex, {
                                label: e.target.value,
                              })
                            }
                            onFocus={() => {
                              if (validation.showHelper) {
                                updateFieldValidation(validationKey, {
                                  ...validation,
                                  showHelper: false,
                                })
                              }

                              // Clear default question label when user focuses on it
                              if (field.label === 'Question Label') {
                                updateField(sectionIndex, fieldIndex, {
                                  label: '',
                                })
                              }
                            }}
                            onBlur={() => {
                              const newValidation = validateFieldLabel(
                                sectionIndex,
                                fieldIndex,
                                field.label
                              )
                              updateFieldValidation(validationKey, {
                                ...newValidation,
                                showHelper: true,
                              })
                            }}
                            fullWidth
                            variant="outlined"
                            size="small"
                            error={validation.error && validation.showHelper}
                            helperText={
                              validation.showHelper ? validation.helperText : ''
                            }
                          />
                          {/* Field source is auto-generated and hidden */}
                          <input type="hidden" value={field.source} />
                        </Grid>

                        {/* Field type specific options */}
                        <Grid item xs={12}>
                          {/* Display field type */}
                          <Typography
                            variant="body2"
                            color="textSecondary"
                            gutterBottom
                          >
                            Field Type:{' '}
                            {field.fieldType === 'yesno'
                              ? 'Yes/No'
                              : field.fieldType === 'dropdown'
                              ? 'Multiple Choice'
                              : field.fieldType === 'text'
                              ? 'Text Box'
                              : field.fieldType === 'attachment'
                              ? 'Attachment'
                              : 'Unknown'}
                          </Typography>

                          {field.fieldType === 'attachment' ? (
                            <Box>
                              <Typography
                                variant="body2"
                                color="textSecondary"
                                gutterBottom
                              >
                                This field will allow vendors to upload PDF or
                                Word documents.
                              </Typography>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  mt: 1,
                                }}
                              >
                                <Button
                                  variant="outlined"
                                  startIcon={<UploadIcon />}
                                  disabled
                                  sx={{ mr: 2 }}
                                >
                                  <Typography>Add Attachment</Typography>
                                </Button>
                                <Typography
                                  variant="caption"
                                  color="textSecondary"
                                >
                                  Preview of how the upload button will appear
                                  to vendors
                                </Typography>
                              </Box>
                            </Box>
                          ) : getEffectiveQuestionnaireType() ===
                            QuestionnaireType.PRELIMINARY ? (
                            <Typography variant="body2" color="textSecondary">
                              This field will have Yes/No answer options.
                            </Typography>
                          ) : (
                            getEffectiveQuestionnaireType() ===
                              QuestionnaireType.RISK &&
                            field.fieldType === 'dropdown' && (
                              <>
                                <Typography variant="body2" gutterBottom>
                                  Answer Options:
                                </Typography>
                                {!field.options ||
                                field.options.length === 0 ? (
                                  <Box sx={{ mb: 1 }}>
                                    <Typography
                                      variant="body2"
                                      color="textSecondary"
                                    >
                                      No options added yet. Add some options
                                      below.
                                    </Typography>
                                  </Box>
                                ) : (
                                  <Box sx={{ mb: 1 }}>
                                    {field.options.map(
                                      (option, optionIndex) => (
                                        <Box
                                          key={optionIndex}
                                          display="flex"
                                          alignItems="center"
                                          mb={1}
                                        >
                                          <TextField
                                            size="small"
                                            value={option.text}
                                            onChange={(e) => {
                                              const newOptions = [
                                                ...(field.options || []),
                                              ]
                                              newOptions[optionIndex] = {
                                                ...newOptions[optionIndex],
                                                text: e.target.value,
                                              }
                                              updateField(
                                                sectionIndex,
                                                fieldIndex,
                                                {
                                                  options: newOptions,
                                                }
                                              )
                                            }}
                                            fullWidth
                                            variant="outlined"
                                            label="Option Text"
                                          />
                                          <TextField
                                            size="small"
                                            value={option.score || ''}
                                            onChange={(e) => {
                                              const { value } = e.target
                                              const score =
                                                value === ''
                                                  ? undefined
                                                  : parseFloat(value)

                                              // Validate score is between 1-20
                                              if (
                                                score !== undefined &&
                                                (Number.isNaN(score) ||
                                                  score < 1 ||
                                                  score > 20)
                                              ) {
                                                return
                                              }

                                              const newOptions = [
                                                ...(field.options || []),
                                              ]
                                              newOptions[optionIndex] = {
                                                ...newOptions[optionIndex],
                                                score,
                                              }
                                              updateField(
                                                sectionIndex,
                                                fieldIndex,
                                                {
                                                  options: newOptions,
                                                }
                                              )
                                            }}
                                            style={{
                                              width: '80px',
                                              marginLeft: '8px',
                                            }}
                                            variant="outlined"
                                            label="Score"
                                            type="number"
                                            inputProps={{
                                              min: 1,
                                              max: 20,
                                              step: '0.1',
                                            }}
                                            helperText={
                                              option.score ? '' : 'Required'
                                            }
                                            error={option.score === undefined}
                                          />
                                          <IconButton
                                            size="small"
                                            onClick={() => {
                                              const newOptions = [
                                                ...(field.options || []),
                                              ]
                                              newOptions.splice(optionIndex, 1)
                                              updateField(
                                                sectionIndex,
                                                fieldIndex,
                                                {
                                                  options: newOptions,
                                                }
                                              )
                                            }}
                                          >
                                            <DeleteIcon />
                                          </IconButton>
                                        </Box>
                                      )
                                    )}
                                  </Box>
                                )}
                                <Button
                                  size="small"
                                  startIcon={<AddIcon />}
                                  onClick={() => {
                                    const newOptions = [
                                      ...(field.options || []),
                                      { text: '', score: undefined },
                                    ]
                                    updateField(sectionIndex, fieldIndex, {
                                      options: newOptions,
                                    })
                                  }}
                                >
                                  <Typography>Add Option</Typography>
                                </Button>
                              </>
                            )
                          )}
                        </Grid>

                        {/* Allow Vendor Comment toggle for all field types except attachment */}
                        {field.fieldType !== 'attachment' && (
                          <Grid item xs={12}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={field.allow_vendor_comment || false}
                                  onChange={(e) =>
                                    updateField(sectionIndex, fieldIndex, {
                                      allow_vendor_comment: e.target.checked,
                                    })
                                  }
                                />
                              }
                              label="Allow Vendor Comment"
                            />
                          </Grid>
                        )}

                        <Grid item xs={6} sm={6}>
                          {getEffectiveQuestionnaireType() ===
                          QuestionnaireType.PRELIMINARY ? (
                            // For prequalification questions, they are always required
                            // Set the field to required but don't show the checkbox
                            (() => {
                              // Ensure the field is always required for prequalification questions
                              if (!field.required) {
                                // Use setTimeout to avoid state updates during render
                                setTimeout(() => {
                                  updateField(sectionIndex, fieldIndex, {
                                    required: true,
                                  })
                                }, 0)
                              }
                              return null // Don't render anything
                            })()
                          ) : (
                            // For risk assessment questions, show the checkbox
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={field.required}
                                  onChange={(e) =>
                                    updateField(sectionIndex, fieldIndex, {
                                      required: e.target.checked,
                                    })
                                  }
                                />
                              }
                              label="Required"
                              sx={{ mr: 0 }}
                            />
                          )}
                        </Grid>
                        <Grid
                          item
                          xs={6}
                          sm={6}
                          display="flex"
                          justifyContent="flex-end"
                        >
                          <IconButton
                            size="small"
                            onClick={() =>
                              deleteField(sectionIndex, fieldIndex)
                            }
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Grid>
                      </Grid>
                    </Paper>
                  )
                })}

                <Button
                  onClick={() => addField(sectionIndex)}
                  startIcon={<AddIcon />}
                  disabled={!areSectionFieldsValid(sectionIndex)}
                >
                  <Typography>Add Field</Typography>
                </Button>
              </AccordionDetails>
            </Accordion>
          ))}
        </>
      )}

      {/* Allow Additional Document Uploads option */}
      <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={allowAdditionalDocumentUploads}
              onChange={(e) =>
                setAllowAdditionalDocumentUploads(e.target.checked)
              }
            />
          }
          label="Allow Additional Document Uploads (Enable suppliers to upload any other relevant files not specifically requested in the questionnaire)"
        />
        <Typography
          variant="caption"
          color="textSecondary"
          sx={{ display: 'block', ml: 4 }}
        >
          This will add an option at the end of the questionnaire for vendors to
          upload additional documents.
        </Typography>
      </Paper>

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mt={2}
      >
        <Button
          onClick={addSection}
          startIcon={<AddIcon />}
          variant="outlined"
          disabled={questionnaireSections.length > 0 && !isQuestionnaireValid()}
        >
          <Typography>
            {questionnaireSections.length === 0
              ? 'Create First Section'
              : 'Add Section'}
          </Typography>
        </Button>

        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {isModal && (
            <Button onClick={onClose} variant="outlined" sx={{ mr: 1 }}>
              <Typography>Cancel</Typography>
            </Button>
          )}
          <Button
            onClick={() => setIsPreviewOpen(true)}
            startIcon={<VisibilityIcon />}
            variant="outlined"
            sx={{ mr: 1 }}
            disabled={!isQuestionnaireValid()}
          >
            <Typography>Preview</Typography>
          </Button>
          <Button
            onClick={saveQuestionnaireForm}
            startIcon={
              saving ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <SaveIcon />
              )
            }
            variant="contained"
            color="primary"
            disabled={!isQuestionnaireValid() || saving}
          >
            <Typography>{saving ? 'Saving...' : 'Save'}</Typography>
          </Button>
        </Box>
      </Box>
    </Box>
  )
}

export default QuestionnaireForm
